/**
 * LockEvent — append-only audit record for every lock/unlock action.
 *
 * No state machine: a LockEvent is created, never modified. The
 * persistence layer enforces immutability (P1.A.2 schema constraint).
 *
 * `providerState` is the lock state the provider (Knox / DPC / MDM)
 * reports just after the action; `deviceState` is what the device
 * itself confirmed via its next signed snapshot. Both may be UNKNOWN at
 * the moment the LockEvent is recorded — they get backfilled by
 * reconciliation (P1.E.2) and the event is amended via a new event
 * (since this aggregate is append-only).
 */
import { z } from "zod";
const lockStageSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
    z.literal(6),
    z.literal(7),
]);
const lockStateSchema = z.enum(["UNLOCKED", "SOFT_LOCKED", "HARD_LOCKED", "UNKNOWN"]);
const lockEventReasonSchema = z.enum([
    "policy_stage_progression",
    "policy_stage_regression",
    "manual_override",
    "payment_received",
    "dispute_raised",
    "dispute_resolved",
    "device_reported",
    "recovery_action",
    "release_ownership",
]);
export const lockEventSchema = z
    .object({
    id: z.string().uuid(),
    deviceId: z.string().uuid(),
    loanId: z.string().uuid(),
    stage: lockStageSchema,
    reason: lockEventReasonSchema,
    triggeredBy: z.string().min(1),
    providerState: lockStateSchema,
    deviceState: lockStateSchema,
    notes: z.string().min(10).optional(),
    occurredAt: z.coerce.date(),
})
    .refine((e) => e.reason !== "manual_override" || (e.notes !== undefined && e.notes.length >= 10), {
    message: "manual_override requires a substantive notes field (≥10 chars)",
    path: ["notes"],
});
export function makeLockEvent(raw) {
    const parsed = lockEventSchema.parse(raw);
    return {
        id: parsed.id,
        deviceId: parsed.deviceId,
        loanId: parsed.loanId,
        stage: parsed.stage,
        reason: parsed.reason,
        triggeredBy: parsed.triggeredBy,
        providerState: parsed.providerState,
        deviceState: parsed.deviceState,
        ...(parsed.notes !== undefined ? { notes: parsed.notes } : {}),
        occurredAt: parsed.occurredAt,
    };
}
//# sourceMappingURL=lock-event.js.map